import { useModal } from "@homebound/beam";
import { pendingFieldProposals } from "src/components/formFields/ai/utils/aiProposalForm";
import { type AiFormAepFragment } from "src/components/formFields/ai/utils/aiProposalForm.generated";
import { parseAiNumber, parseAiString } from "src/components/formFields/ai/utils/bindAiProposal";
import { useFeatureFlag } from "src/contexts/FeatureFlags/FeatureFlagContext";
import {
  AiEntityProposalType,
  AiFieldProposalStatus,
  FeatureFlagType,
  SaveTakeoffLineItemInput,
} from "src/generated/graphqlTypes";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { PlanPackageAiReviewBanner } from "src/routes/libraries/plan-package-v2/components/PlanPackageAiReviewBanner";
import {
  useTakeoffsAiReviewBanner_ResolveProposalsMutation,
  useTakeoffsAiReviewBannerQuery,
} from "src/routes/libraries/plan-package-v2/components/takeoffs/TakeoffsAiReviewBanner.generated";
import { PlanPackagePageV2MetadataDocument } from "src/routes/libraries/plan-package-v2/PlanPackagePageV2.generated";
import { useTakeoffsStore } from "src/routes/libraries/plan-package/takeoffs/TakeoffsManagerContext";
import { fail } from "src/utils";

type ReviewDecision = AiFieldProposalStatus.Accepted | AiFieldProposalStatus.Rejected;

/**
 * Accept All / Ignore All for the plan's pending AI takeoff lines.
 *
 * Proposals come from the plan package rather than the table's rows, since the table only loads a page of lines.
 */
export function TakeoffsAiReviewBanner() {
  const planPackageId = useTakeoffsStore((state) => state.planPackage.id);
  const canEditLineItems = useTakeoffsStore((state) => state.planPackage.canEditLineItems);
  const saveTli = useTakeoffsStore((state) => state.saveTli);
  const isTakeoffAiEnabled = useFeatureFlag(FeatureFlagType.PlanPackagesTakeoffAi);
  const { data } = useTakeoffsAiReviewBannerQuery({ variables: { planPackageId }, skip: !isTakeoffAiEnabled });
  const [resolveProposals] = useTakeoffsAiReviewBanner_ResolveProposalsMutation();
  const { openModal } = useModal();

  // The resolve mutation returns each proposal's new status, which drops it from here without a refetch
  const proposals = (data?.planPackage.aiProposal?.entityProposals ?? []).filter(
    (aep) => pendingFieldProposals(aep.fieldProposals).nonEmpty,
  );
  // There's no first-import marker, so all-CREATE proposals get the first-import copy
  const hasOnlyCreates = proposals.every((aep) => aep.type.code === AiEntityProposalType.Create);

  /**
   * Resolving a proposal only records the decision, so we write what it means for the lines ourselves.
   * Lines go first, so a failed save leaves the proposals pending to try again.
   */
  async function review(decision: ReviewDecision) {
    const lines = proposals.flatMap((aep) => toLineInputs(aep, decision));
    if (lines.nonEmpty) await saveTli(lines);
    await resolveProposals({
      variables: {
        input: proposals.flatMap((aep) =>
          pendingFieldProposals(aep.fieldProposals).map(({ id }) => ({ id, status: decision })),
        ),
      },
      // The page query drives the tab's AI badge
      refetchQueries: [PlanPackagePageV2MetadataDocument],
    });
  }

  function reconcileAll(decision: ReviewDecision) {
    if (decision === AiFieldProposalStatus.Accepted) return review(decision);
    openModal({
      content: (
        <ConfirmationModal
          title="Ignore All AI-Generated Changes?"
          label="Ignore All AI Lines"
          confirmationMessage="'Ignoring all' will remove all AI-generated changes from this takeoff. You won't be able to restore these changes after they're ignored."
          onConfirmAction={() => review(decision)}
        />
      ),
    });
  }

  return (
    <PlanPackageAiReviewBanner
      hasPending={proposals.nonEmpty}
      onReconcileAll={reconcileAll}
      disabled={disableBasedOnPotentialOperation(canEditLineItems)}
      {...(hasOnlyCreates
        ? {
            title: "AI-generated takeoff scope is ready for review.",
            message: "Review and refine the generated lines below, and accept them when you're ready.",
          }
        : {
            title: "Review changes to your takeoffs found in your latest document capture.",
            message: "Review the changes highlighted below.",
          })}
    />
  );
}

/** What a decision writes to the line itself. */
function toLineInputs(aep: AiFormAepFragment, decision: ReviewDecision): SaveTakeoffLineItemInput[] {
  const { id } = aep.entity;
  const accepted = decision === AiFieldProposalStatus.Accepted;
  // The import already created a CREATE's line, so ignoring it removes the line
  if (aep.type.code === AiEntityProposalType.Create) return accepted ? [] : [{ id, active: false }];
  // The backend only proposes CREATEs and UPDATEs for lines, so anything else would resolve without being applied
  if (aep.type.code !== AiEntityProposalType.Update) fail(`Unexpected ${aep.type.code} proposal for ${id}`);
  if (!accepted) return [];
  // An UPDATE leaves the line as-is, so accepting writes the proposed quantity, UoM, and task
  const pending = pendingFieldProposals(aep.fieldProposals);
  const proposed = (fieldName: string) => pending.find((fp) => fp.fieldName === fieldName)?.fieldValue;
  const quantity = parseAiNumber(proposed("quantity"));
  const unitOfMeasureName = parseAiString(proposed("unitOfMeasure"));
  const taskName = parseAiString(proposed("task"));
  return [
    {
      id,
      ...(quantity !== undefined ? { quantity } : {}),
      ...(unitOfMeasureName ? { unitOfMeasureName } : {}),
      ...(taskName ? { taskName } : {}),
    },
  ];
}
